/**
 * Home.tsx — Página principal
 *
 * Grafo 3D em fullscreen + chat global flutuante + controles de força.
 */

import { useEffect, useState } from 'react'
import { collection, getDocs, query, limit } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Graph3D, type GraphData, type GraphNode } from '../components/Graph3D'
import { NodePanel } from '../components/NodePanel'
import { ChatGlobal } from '../components/ChatGlobal'

interface VaultNote {
  id: string
  title: string
  category: string
  path: string
  wikilinks_out: string[]
}

function slugify(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  )
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [breakpoint])
  return isMobile
}

export function Home() {
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] })
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [glowNodeIds, setGlowNodeIds] = useState<string[]>([])
  const [loadingGraph, setLoadingGraph] = useState(true)
  const isMobile = useIsMobile()

  // Forças do grafo (ajustáveis em runtime)
  const [chargeStrength, setChargeStrength] = useState(-120)
  const [linkStrength, setLinkStrength] = useState(0.6)
  const [linkDistance, setLinkDistance] = useState(60)
  const [centerStrength, setCenterStrength] = useState(0.05)
  const [showForces, setShowForces] = useState(false)
  const [legendCollapsed, setLegendCollapsed] = useState(isMobile)

  useEffect(() => {
    async function loadGraph() {
      try {
        const snap = await getDocs(query(collection(db, 'vault_notes'), limit(1000)))

        const notes: VaultNote[] = snap.docs.map((d) => ({
          id: d.id,
          title: d.data().title as string,
          category: d.data().category as string,
          path: d.data().path as string,
          wikilinks_out: (d.data().wikilinks_out as string[]) ?? [],
        }))

        // Mapa slug → id (wikilinks_out usa slug, não id)
        const slugToId = new Map<string, string>()
        for (const n of notes) {
          const slugTitle = slugify(n.title)
          if (slugTitle && !slugToId.has(slugTitle)) slugToId.set(slugTitle, n.id)
          // também mapeia pelo nome do arquivo (sem extensão)
          const fileName = n.path?.split('/').pop()?.replace(/\.md$/i, '')
          if (fileName) {
            const slugFile = slugify(fileName)
            if (slugFile && !slugToId.has(slugFile)) slugToId.set(slugFile, n.id)
          }
          // mapeia o próprio id pra ele mesmo (caso wikilink já venha como id)
          slugToId.set(n.id, n.id)
        }

        const nodes: GraphNode[] = notes.map((n) => ({
          id: n.id,
          title: n.title,
          category: n.category,
          path: n.path,
        }))

        const links = notes.flatMap((n) =>
          n.wikilinks_out
            .map((target) => slugToId.get(target) ?? slugToId.get(slugify(target)))
            .filter((targetId): targetId is string => !!targetId && targetId !== n.id)
            .map((targetId) => ({ source: n.id, target: targetId })),
        )

        setGraphData({ nodes, links })
      } catch (err) {
        console.error('[Home] Erro ao carregar grafo:', err)
      } finally {
        setLoadingGraph(false)
      }
    }
    loadGraph()
  }, [])

  const handleNodeClick = (node: GraphNode) => setSelectedNodeId(node.id)

  const handleCitedNodes = (nodeIds: string[]) => {
    setGlowNodeIds(nodeIds)
    setTimeout(() => setGlowNodeIds([]), 8000)
  }

  if (loadingGraph && graphData.nodes.length === 0) {
    return <div className="loading-screen">Carregando vault...</div>
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Graph3D
        data={graphData}
        glowNodeIds={glowNodeIds}
        selectedNodeId={selectedNodeId}
        onNodeClick={handleNodeClick}
        chargeStrength={chargeStrength}
        linkStrength={linkStrength}
        linkDistance={linkDistance}
        centerStrength={centerStrength}
      />

      {/* Painel lateral / bottom-sheet */}
      {selectedNodeId && (
        <div
          className={isMobile ? 'node-panel-mobile' : ''}
          style={{
            position: 'absolute',
            ...(isMobile
              ? { bottom: 0, left: 0, right: 0, height: '70vh', zIndex: 50 }
              : { top: 0, right: 0, height: '100%', zIndex: 50 }),
          }}
        >
          <NodePanel noteId={selectedNodeId} onClose={() => setSelectedNodeId(null)} />
        </div>
      )}

      {/* Contador */}
      <div
        style={{
          position: 'absolute',
          top: isMobile ? '8px' : '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: isMobile ? '10px' : '11px',
          color: '#9090b0',
          background: 'rgba(8,8,16,0.75)',
          padding: '4px 12px',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {graphData.nodes.length} notas · {graphData.links.length} conexões
      </div>

      {/* Botões de ação canto superior direito (mobile) */}
      {isMobile && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            gap: '6px',
            zIndex: 40,
          }}
        >
          <ToolButton label="⚙" active={showForces} onClick={() => setShowForces((v) => !v)} />
        </div>
      )}

      {/* Painel de Forças */}
      {(showForces || !isMobile) && (
        <div
          style={{
            position: 'absolute',
            top: isMobile ? '52px' : '60px',
            right: isMobile ? '8px' : '16px',
            left: isMobile ? '8px' : 'auto',
            width: isMobile ? 'auto' : '240px',
            maxWidth: isMobile ? 'none' : '300px',
            background: 'rgba(13,13,30,0.95)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px',
            padding: '12px',
            backdropFilter: 'blur(10px)',
            zIndex: 41,
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              color: '#57f5c3',
              fontWeight: 600,
              marginBottom: '10px',
              letterSpacing: '0.05em',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>FORÇAS DO GRAFO</span>
            {!isMobile && (
              <button
                onClick={() => setShowForces((v) => !v)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#5a5a80',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: 0,
                }}
                title="Esconder/mostrar"
              >
                {showForces ? '−' : '+'}
              </button>
            )}
          </div>

          {(showForces || !isMobile) && (
            <>
              <ForceSlider
                label="Repulsão"
                hint="Quanto mais negativo, mais os nós se afastam"
                value={chargeStrength}
                min={-500}
                max={-10}
                step={10}
                onChange={setChargeStrength}
                display={chargeStrength.toString()}
              />
              <ForceSlider
                label="Atração nos links"
                hint="Força de coesão entre nós conectados (0–1)"
                value={linkStrength}
                min={0}
                max={1}
                step={0.05}
                onChange={setLinkStrength}
                display={linkStrength.toFixed(2)}
              />
              <ForceSlider
                label="Distância dos links"
                hint="Distância ideal entre nós conectados"
                value={linkDistance}
                min={20}
                max={200}
                step={5}
                onChange={setLinkDistance}
                display={linkDistance.toString()}
              />
              <ForceSlider
                label="Gravidade central"
                hint="Quanto puxa tudo pro centro"
                value={centerStrength}
                min={0}
                max={0.5}
                step={0.01}
                onChange={setCenterStrength}
                display={centerStrength.toFixed(2)}
              />
              <button
                onClick={() => {
                  setChargeStrength(-120)
                  setLinkStrength(0.6)
                  setLinkDistance(60)
                  setCenterStrength(0.05)
                }}
                style={{
                  marginTop: '6px',
                  width: '100%',
                  padding: '6px',
                  fontSize: '11px',
                  background: 'rgba(87, 245, 195, 0.1)',
                  border: '1px solid rgba(87, 245, 195, 0.3)',
                  borderRadius: '4px',
                  color: '#57f5c3',
                  cursor: 'pointer',
                }}
              >
                resetar padrões
              </button>
            </>
          )}
        </div>
      )}

      {/* Legenda */}
      <CategoryLegend
        isMobile={isMobile}
        collapsed={legendCollapsed}
        onToggle={() => setLegendCollapsed((v) => !v)}
      />

      {/* Chat */}
      <ChatGlobal
        onCitedNodes={handleCitedNodes}
        focusNoteId={selectedNodeId}
        focusNoteTitle={
          selectedNodeId
            ? graphData.nodes.find((n) => n.id === selectedNodeId)?.title ?? null
            : null
        }
        onClearFocus={() => setSelectedNodeId(null)}
      />
    </div>
  )
}

function ToolButton({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '36px',
        height: '36px',
        background: active ? 'rgba(87, 245, 195, 0.2)' : 'rgba(13,13,30,0.85)',
        border: active
          ? '1px solid rgba(87, 245, 195, 0.4)'
          : '1px solid rgba(255,255,255,0.1)',
        borderRadius: '6px',
        color: active ? '#57f5c3' : '#cfd0e0',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)',
      }}
    >
      {label}
    </button>
  )
}

function ForceSlider({
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
  display,
}: {
  label: string
  hint?: string
  value: number
  min: number
  max: number
  step: number
  onChange: (v: number) => void
  display: string
}) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px',
        }}
      >
        <span style={{ fontSize: '11px', color: '#cfd0e0' }} title={hint}>
          {label}
        </span>
        <span style={{ fontSize: '10px', color: '#5a5a80', fontFamily: 'monospace' }}>
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: '100%',
          accentColor: '#57f5c3',
          background: 'transparent',
        }}
      />
    </div>
  )
}

function CategoryLegend({
  isMobile,
  collapsed,
  onToggle,
}: {
  isMobile: boolean
  collapsed: boolean
  onToggle: () => void
}) {
  const categories = [
    { label: 'Conhecimento', color: '#57f5c3' },
    { label: 'Clientes', color: '#FF6B35' },
    { label: 'Projetos', color: '#9C27B0' },
    { label: 'Pessoas', color: '#4CAF50' },
    { label: 'Agência', color: '#FFC107' },
    { label: 'Tecnologia', color: '#2196F3' },
    { label: 'Operações', color: '#E91E63' },
    { label: 'Meta', color: '#607D8B' },
  ]

  // Mobile: barra horizontal no topo, abaixo da navbar (48px)
  // Desktop: card vertical no canto inferior esquerdo
  if (isMobile) {
    return (
      <div
        style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          right: '60px',
          background: 'rgba(8,8,16,0.88)',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.06)',
          zIndex: 30,
          padding: collapsed ? '6px 10px' : '8px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          overflow: 'hidden',
        }}
      >
        <button
          onClick={onToggle}
          aria-label="Toggle legenda"
          style={{
            background: 'none',
            border: 'none',
            color: '#cfd0e0',
            cursor: 'pointer',
            padding: 0,
            fontSize: '12px',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {collapsed ? '▸' : '▾'} <span style={{ fontSize: '10px', color: '#9090b0', marginLeft: '2px' }}>cat.</span>
        </button>
        {!collapsed && (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              overflowX: 'auto',
              flex: 1,
              scrollbarWidth: 'none',
            }}
          >
            {categories.map((c) => (
              <div
                key={c.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: c.color,
                    boxShadow: `0 0 4px ${c.color}`,
                  }}
                />
                <span style={{ fontSize: '10px', color: '#cfd0e0', whiteSpace: 'nowrap' }}>
                  {c.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Desktop
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '24px',
        left: '24px',
        background: 'rgba(8,8,16,0.85)',
        padding: collapsed ? '8px 12px' : '10px 14px',
        borderRadius: '8px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.06)',
        zIndex: 30,
        minWidth: collapsed ? 'auto' : '140px',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          background: 'none',
          border: 'none',
          color: '#9090b0',
          fontSize: '10px',
          cursor: 'pointer',
          padding: 0,
          marginBottom: collapsed ? 0 : '8px',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        {collapsed ? '▸' : '▾'} categorias
      </button>
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {categories.map((c) => (
            <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: c.color,
                  boxShadow: `0 0 6px ${c.color}`,
                }}
              />
              <span style={{ fontSize: '11px', color: '#cfd0e0' }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
